/**
 * WaitingDialog — aguardando a confirmação do email / conflito de conta (porta de
 * `showWaiting` [application.js:2705]). Acionado quando: sync.actived === false no boot,
 * status `conflit` no /me, ou signup com status pending/conflit.
 *
 *  - eminer: "Confirme seu email <b>email</b>" + preloader + "verifique o spam" + botão
 *    trocar email; POLLING GET /app/checkemail?address= a cada 2s — { active:true } →
 *    marca actived no sync e recarrega a página.
 *  - lead-machine: com `remaining` (data futura) mostra o tempo de espera que ANDA (a cada
 *    30 s) e quando libera; zerado, "Pronto" + "Já pode reativar"; sem remaining, a tela de
 *    conflito ("Seu acesso foi para outro computador"); botão reativar.
 * Visual da proposta "Elements" (17/10/2026): página centralizada (AuthLayout). No eMiner,
 * confirmado o email, aparece "Email confirmado" antes de ir ao painel.
 *
 * Movimento (23/10/2026): a espera sem número é o giro com o texto; zerado o tempo, "Pronto"
 * entra com fade (.ds-aparece) e "Reativar agora", que fica amarelo, solta o halo uma vez
 * (.ds-pulso-acao, M12 b); confirmado o email, o ✓ e o giro de "Levando você ao painel…".
 */
import { useEffect, useState } from 'react';
import { checkEmail } from '../../api/useOnboarding';
import { setSync } from '../../lib/extensionBridge';
import { useBrand } from '../../hooks/useBrand';
import { AuthLayout, Html, StateIcon, SuccessBurst } from '../../components/screen/Screen';
import { escapeHtml, useHaloUmaVez, useScreenText } from '../../components/screen/screenText';

interface WaitingDialogProps {
  email: string;
  /** Data (ISO) até a qual o usuário precisa aguardar (sync.remaining) — lead-machine. */
  remaining?: string;
  /** Vai para o formulário de ativação (showOnboarding). */
  onReactivate: () => void;
}

export function WaitingDialog({ email, remaining, onReactivate }: WaitingDialogProps): JSX.Element {
  const brand = useBrand();
  const isLeadMachine = brand.source === 'lead-machine';
  if (!isLeadMachine) return <ConfirmEmail email={email} onChange={onReactivate} />;
  if (remaining && !Number.isNaN(new Date(remaining).getTime())) {
    return <WaitTimer remaining={remaining} onReactivate={onReactivate} />;
  }
  return <Conflict onReactivate={onReactivate} />;
}

function WaitTimer({
  remaining,
  onReactivate,
}: {
  remaining: string;
  onReactivate: () => void;
}): JSX.Element {
  const { tx, date, time } = useScreenText();
  const until = new Date(remaining);
  const [now, setNow] = useState(() => Date.now());
  const left = until.getTime() - now;
  const done = left <= 0;

  useEffect(() => {
    if (done) return;
    // Minuto a minuto basta; 30 s deixa a virada do zero rápida.
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, [done]);

  const minutes = Math.max(0, Math.ceil(left / 60000));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const label =
    minutes <= 1 ? tx('wait_less') : h > 0 ? tx('wait_hm', { h, m }) : tx('wait_m', { m });
  const halo = useHaloUmaVez(done);

  return (
    <AuthLayout>
      <section className="as-card" aria-labelledby="as-title">
        <StateIcon icon="schedule" />
        <div className="as-head">
          <h1 id="as-title">{tx('wait_title')}</h1>
          <p className="as-card__text">{tx('wait_text')}</p>
        </div>
        {done ? (
          <p className="as-note as-note--success ds-aparece" role="status">
            <span className="material-symbols-rounded" aria-hidden="true">
              check_circle
            </span>
            {tx('wait_done')}
          </p>
        ) : (
          <div className="as-timer" role="timer" aria-live="polite">
            <strong>{label}</strong>
            <span>{tx('wait_until', { date: date(until), time: time(until) })}</span>
          </div>
        )}
        <button
          type="button"
          id="button_send"
          className={`as-btn as-btn--block ds-resposta ${done ? 'as-btn--primary' : 'as-btn--outline'}${halo ? ' ds-pulso-acao' : ''}`}
          onClick={onReactivate}
        >
          {done ? (
            <span className="material-symbols-rounded" aria-hidden="true">
              refresh
            </span>
          ) : null}
          {done ? tx('reactivate_now') : tx('reactivate')}
        </button>
      </section>
    </AuthLayout>
  );
}

function Conflict({ onReactivate }: { onReactivate: () => void }): JSX.Element {
  const { tx } = useScreenText();
  return (
    <AuthLayout>
      <section className="as-card" aria-labelledby="as-title">
        <StateIcon icon="devices_other" tone="gray" />
        <div className="as-head">
          <h1 id="as-title">{tx('conf_title')}</h1>
          <p className="as-card__text">{tx('conf_text')}</p>
        </div>
        <button
          type="button"
          id="button_send"
          className="as-btn as-btn--primary as-btn--block ds-resposta"
          onClick={onReactivate}
        >
          <span className="material-symbols-rounded" aria-hidden="true">
            refresh
          </span>
          {tx('reactivate')}
        </button>
      </section>
    </AuthLayout>
  );
}

function ConfirmEmail({ email, onChange }: { email: string; onChange: () => void }): JSX.Element {
  const { tx } = useScreenText();
  const [confirmed, setConfirmed] = useState(false);

  // Polling da confirmação (legado: a cada 2s enquanto o diálogo existe).
  useEffect(() => {
    let stop = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = (): void => {
      void checkEmail(email).then((active) => {
        if (stop) return;
        if (active) {
          setSync({ actived: true });
          setConfirmed(true);
          // href='/' (não reload) — o WaitingDialog pode estar sobre /onboarding (signup
          // pending); recarregar manteria a rota e reabriria o form. Volta para a home.
          setTimeout(() => {
            window.location.href = '/';
          }, 1500);
          return;
        }
        timer = setTimeout(tick, 2000);
      });
    };
    timer = setTimeout(tick, 200);
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, [email]);

  if (confirmed) {
    return (
      <AuthLayout>
        <section className="as-card as-card--center">
          <SuccessBurst title={tx('confirmed')} text={tx('going')} progress />
        </section>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <section className="as-card" aria-labelledby="as-title">
        <StateIcon icon="mark_email_unread" />
        <h1 id="as-title">{tx('confirm_title')}</h1>
        <Html className="as-card__text" html={tx('confirm_text', { email: escapeHtml(email) })} />
        <p className="as-pulse" role="status">
          <span className="as-pulse__giro ds-giro material-symbols-rounded" aria-hidden="true">
            progress_activity
          </span>
          {tx('confirm_waiting')}
        </p>
        <p className="as-hint as-hint--icon">
          <span className="material-symbols-rounded" aria-hidden="true">
            info
          </span>
          {tx('confirm_spam')}
        </p>
        <button
          type="button"
          id="button_send"
          className="as-btn as-btn--outline as-btn--block ds-resposta"
          onClick={onChange}
        >
          {tx('confirm_change')}
        </button>
      </section>
    </AuthLayout>
  );
}
